import React from 'react';
import type { BlockNode } from '../../types';
import type { BlockRenderProps, BlockRendererComponent } from './types';
import {
  ContainerRenderer,
  Grid2ColRenderer,
  SectionRenderer,
  SpacerRenderer,
  DividerRenderer,
  HeroSplitRenderer,
} from './LayoutRenderers';
import {
  SiteNavbarRenderer,
  SiteFooterRenderer,
} from './SiteChromeRenderers';
import {
  HeadingRenderer,
  ParagraphRenderer,
  ButtonRenderer,
  RichTextBlockRenderer,
  MathFormulaBlockRenderer,
} from './TypographyRenderers';
import {
  DigitalProductCardRenderer,
  DigitalProductListRenderer,
  StorefrontCheckoutBlockRenderer,
  PricingTableRenderer,
} from './CommerceRenderers';
import {
  TopperCardRenderer,
  ToppersShowcaseRenderer,
  TestimonialsRenderer,
  FacultyShowcaseRenderer,
  AccordionFAQRenderer,
} from './AcademicsRenderers';
import {
  LegalAgreementBlockRenderer,
  ConsentCheckboxBlockRenderer,
  CookieBannerBlockRenderer,
} from './LegalRenderers';
import {
  StatsCounterRenderer,
  CountdownTimerRenderer,
  CallToActionBannerRenderer,
} from './MarketingRenderers';
import {
  VideoPlayerRenderer,
  ImageGalleryRenderer,
} from './MediaRenderers';
import {
  ConversionFormBlockRenderer,
  SchoolNoticeBoardBlockRenderer,
  CoachCourseCatalogBlockRenderer,
  AppProductCatalogBlockRenderer,
  FeePaymentPortalBlockRenderer,
  AdmissionsStatusTrackerBlockRenderer,
} from './ERPRenderers';

export const BLOCK_RENDERERS: Record<string, BlockRendererComponent> = {
  // Layout & Permanent Chrome
  SiteNavbar: SiteNavbarRenderer,
  SiteFooter: SiteFooterRenderer,
  Container: ContainerRenderer,
  Grid2Col: Grid2ColRenderer,
  Section: SectionRenderer,
  Spacer: SpacerRenderer,
  Divider: DividerRenderer,
  HeroSplit: HeroSplitRenderer,

  // Typography
  Heading: HeadingRenderer,
  Paragraph: ParagraphRenderer,
  Button: ButtonRenderer,
  RichTextBlock: RichTextBlockRenderer,
  MathFormulaBlock: MathFormulaBlockRenderer,

  // Commerce & Digital Products
  DigitalProductCard: DigitalProductCardRenderer,
  DigitalProductList: DigitalProductListRenderer,
  StorefrontCheckoutBlock: StorefrontCheckoutBlockRenderer,
  PricingTable: PricingTableRenderer,

  // Academics, Faculty & Toppers
  TopperCard: TopperCardRenderer,
  ToppersShowcase: ToppersShowcaseRenderer,
  CoachToppersWallBlock: ToppersShowcaseRenderer,
  Testimonials: TestimonialsRenderer,
  FacultyShowcase: FacultyShowcaseRenderer,
  AccordionFAQ: AccordionFAQRenderer,

  // Conversion & Marketing
  StatsCounter: StatsCounterRenderer,
  CountdownTimer: CountdownTimerRenderer,
  CallToActionBanner: CallToActionBannerRenderer,

  // Media
  VideoPlayer: VideoPlayerRenderer,
  ImageGallery: ImageGalleryRenderer,

  // Legal & Compliance
  LegalAgreementBlock: LegalAgreementBlockRenderer,
  ConsentCheckboxBlock: ConsentCheckboxBlockRenderer,
  CookieBannerBlock: CookieBannerBlockRenderer,

  // ERP & Live Data
  ConversionFormBlock: ConversionFormBlockRenderer,
  SchoolNoticeBoardBlock: SchoolNoticeBoardBlockRenderer,
  CoachCourseCatalogBlock: CoachCourseCatalogBlockRenderer,
  AppProductCatalogBlock: AppProductCatalogBlockRenderer,
  FeePaymentPortalBlock: FeePaymentPortalBlockRenderer,
  AdmissionsStatusTrackerBlock: AdmissionsStatusTrackerBlockRenderer,
};

export const DefaultFallbackRenderer: React.FC<BlockRenderProps> = ({ node, children }) => {
  return (
    <div className="p-4 border border-border rounded-lg bg-card text-card-foreground">
      <div className="text-xs font-semibold text-muted-foreground uppercase">{node.type}</div>
      {children}
    </div>
  );
};

export function getBlockRenderer(blockType: string): BlockRendererComponent {
  return BLOCK_RENDERERS[blockType] || DefaultFallbackRenderer;
}

export * from './types';
